import {
  ALL_OHLC_RESOLUTIONS,
  DEFAULT_OHLC_RESOLUTIONS,
  type DateRangeOrLookback,
  HistoricalPriceChart,
  MixpanelEvent,
  MixpanelEventSource,
  type ResolutionEnumNoPrefix,
  Spinner,
  useMixpanel,
  useWLOrgConfigContext,
  useWLSymbol,
} from '@talos/kyoko';
import type { AgAnnotation, AgPriceVolumeChartType } from 'ag-charts-community';
import { useCallback, useState } from 'react';
import { useLocalStorage } from 'react-use';
import { useOMSUtilsContext } from '../../providers/OMSStateProvider';

export const OHLCVChart = ({
  showSymbolSelector,
  showVolume,
}: {
  showSymbolSelector?: boolean;
  showVolume?: boolean;
}) => {
  const { config } = useWLOrgConfigContext();
  const { symbol } = useWLSymbol();
  const { setSymbol } = useOMSUtilsContext();
  // Resolution and chart type are stored in local storage to persist the values across page reloads
  const [chartType, setChartType] = useLocalStorage<AgPriceVolumeChartType>('ohlcv-chart-type', 'candlestick');
  const [resolution, setResolution] = useLocalStorage<ResolutionEnumNoPrefix>('ohlcv-resolution', '1H');
  const [annotations, setAnnotations] = useLocalStorage<AgAnnotation[] | undefined>(
    `ohlcv-annotations-${symbol}`,
    undefined
  );
  const [dateRange, setDateRange] = useState<DateRangeOrLookback>();
  const mixpanel = useMixpanel();
  const handleResolutionChange = (newResolution: ResolutionEnumNoPrefix) => {
    setResolution(newResolution);
  };

  const handleSymbolChange = useCallback(
    (symbol?: string) => {
      if (symbol) {
        mixpanel.track(MixpanelEvent.ChangeSymbol, { source: MixpanelEventSource.CandleSticks });
        setSymbol(symbol);
      }
    },
    [setSymbol, mixpanel]
  );

  const handleAnnotationsChange = useCallback(
    (newAnnotations?: AgAnnotation[] | undefined) => {
      mixpanel.track(MixpanelEvent.ChangeAnnotations, { source: MixpanelEventSource.CandleSticks });
      setAnnotations(newAnnotations);
    },
    [setAnnotations, mixpanel]
  );

  const handleChartTypeChange = useCallback(
    (newChartType: AgPriceVolumeChartType) => {
      mixpanel.track(MixpanelEvent.ChangeChartType, { source: MixpanelEventSource.CandleSticks });
      setChartType(newChartType);
    },
    [setChartType, mixpanel]
  );

  return symbol ? (
    <HistoricalPriceChart
      key={`${symbol}-${resolution}`}
      symbol={symbol}
      onChangeSymbol={showSymbolSelector ? handleSymbolChange : undefined}
      resolution={resolution}
      availableResolutions={config.enableNonNativeOHLCResolutions ? ALL_OHLC_RESOLUTIONS : DEFAULT_OHLC_RESOLUTIONS}
      onResolutionChange={handleResolutionChange}
      dateRange={dateRange}
      onDateRangeChange={setDateRange}
      onAnnotationsChange={handleAnnotationsChange}
      annotations={annotations}
      chartType={chartType}
      onChartTypeChange={handleChartTypeChange}
      showVolume={showVolume}
      h="100%"
    />
  ) : (
    <Spinner />
  );
};
